import type {
  TwilioError,
  LocalParticipant as TwilioLocalParticipant,
  LocalTrackPublication as TwilioLocalTrackPublication,
  RemoteDataTrack as TwilioRemoteDataTrack,
  RemoteParticipant as TwilioRemoteParticipant,
  RemoteTrack as TwilioRemoteTrack,
  RemoteTrackPublication as TwilioRemoteTrackPublication,
  RemoteVideoTrack as TwilioRemoteVideoTrack,
  Room as TwilioRoom,
  Track as TwilioTrack,
} from 'twilio-video';

import { RoomEvent } from '../../core/room/types';

// source: twilio-video/tsdef/Room.d.ts
export type TwilioRoomEventArgsMap = {
  disconnected: [TwilioRoom, TwilioError];
  dominantSpeakerChanged: [TwilioRemoteParticipant | null];
  participantConnected: [TwilioRemoteParticipant];
  participantDisconnected: [TwilioRemoteParticipant];
  participantReconnected: [TwilioRemoteParticipant];
  participantReconnecting: [TwilioRemoteParticipant];
  reconnected: [];
  reconnecting: [TwilioError];
  recordingStarted: [TwilioRemoteVideoTrack, TwilioRemoteParticipant];
  trackDisabled: [TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackEnabled: [TwilioRemoteTrackPublication];
  trackMessage: [string, TwilioRemoteDataTrack, TwilioRemoteParticipant];
  trackPublished: [TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackPublishPriorityChanged: [TwilioTrack.Priority, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackStarted: [TwilioRemoteTrack, TwilioRemoteParticipant];
  trackSubscribed: [TwilioRemoteTrack, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackSubscriptionFailed: [TwilioError, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackSwitchedOff: [TwilioRemoteTrack, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackSwitchedOn: [TwilioRemoteTrack, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackUnpublished: [TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackUnsubscribed: [TwilioRemoteTrack, TwilioRemoteTrackPublication, TwilioRemoteParticipant];
  trackWarning: [string, TwilioLocalTrackPublication, TwilioLocalParticipant];
  trackWarningsCleared: [TwilioLocalTrackPublication, TwilioLocalParticipant];
};

export type TwilioRoomEvent = keyof TwilioRoomEventArgsMap;
export type TwilioRoomEventHandlerMap = {
  [K in keyof TwilioRoomEventArgsMap]: (...args: TwilioRoomEventArgsMap[K]) => void;
};

export type TwilioRoomEventHandler<K extends RoomEvent> = TwilioRoomEventHandlerMap[K];

/**
 * Contains the list of actively used standard events mapped to Twilio events.
 */
export const STANDARD_ROOM_EVENT_TO_TWILIO_ROOM_EVENT_MAP: Record<RoomEvent, TwilioRoomEvent> = {
  participantConnected: 'participantConnected',
  participantReconnecting: 'participantReconnecting',
  participantReconnected: 'participantReconnected',
  participantDisconnected: 'participantDisconnected',
  disconnected: 'disconnected',
  dominantSpeakerChanged: 'dominantSpeakerChanged',
  reconnecting: 'reconnecting',
  reconnected: 'reconnected',
} as const;

export type StandardRoomEventToTwilioRoomEventMap = typeof STANDARD_ROOM_EVENT_TO_TWILIO_ROOM_EVENT_MAP;
